/**
 * <s-product-gallery-slider> — Product media carousel with touch swipe, arrow navigation, and thumbnail synchronization.
 */
class SProductGallerySlider extends HTMLElement {
  #track;
  #slides;
  #prevBtn;
  #nextBtn;
  #thumbs;
  #observer;
  #ac;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.#prevBtn = this.querySelector('[data-gallery-prev]');
    this.#nextBtn = this.querySelector('[data-gallery-next]');
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#prevBtn?.addEventListener('click', () => this.#step(-1), { signal });
    this.#nextBtn?.addEventListener('click', () => this.#step(1), { signal });

    this.#thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this.goToSlide(idx), { signal });
    });

    const galleryDataEl = this.querySelector('[data-variant-gallery-data]')
      || document.querySelector('[data-variant-gallery-data]');
    let variantDataList = [];
    if (galleryDataEl) {
      try {
        variantDataList = JSON.parse(galleryDataEl.textContent);
      } catch (e) {
        console.error('[s-product-gallery-slider] Failed to parse gallery data:', e);
      }
    }

    const goToVariantMedia = (identifier) => {
      if (!identifier || !variantDataList.length) return;
      const raw = String(identifier).trim().toLowerCase();
      const matched = variantDataList.find((v) => {
        if (!v) return false;
        if (String(v.id) === raw) return true;
        if (v.title && String(v.title).trim().toLowerCase() === raw) return true;
        if (v.option1 && String(v.option1).trim().toLowerCase() === raw) return true;
        return false;
      });
      if (!matched) return;

      let targetIndex = -1;
      if (matched.media_id) {
        targetIndex = this.#slides.findIndex(
          (slide) => slide.getAttribute('data-media-id') === String(matched.media_id)
        );
      }

      if (targetIndex === -1 && matched.image_filename) {
        targetIndex = this.#slides.findIndex(
          (slide) => slide.getAttribute('data-media-filename') === matched.image_filename
        );
      }

      if (targetIndex !== -1) {
        this.goToSlide(targetIndex);
      }
    };

    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target) return;
      const variantId = target.dataset?.variantId;
      const value = target.value;
      const selectedOptionVal = target.selectedOptions?.[0]?.value;

      if (variantId) {
        goToVariantMedia(variantId);
      } else if (value) {
        goToVariantMedia(value);
      } else if (selectedOptionVal) {
        goToVariantMedia(selectedOptionVal);
      }
    }, { signal });

    document.addEventListener('click', (e) => {
      const pill = e.target.closest('label, button, [data-variant-id]');
      if (!pill) return;
      const input = pill.querySelector('input') || pill;
      const variantId = input.dataset?.variantId || pill.dataset?.variantId;
      const textVal = input.value || pill.textContent?.trim();
      if (variantId) {
        goToVariantMedia(variantId);
      } else if (textVal) {
        goToVariantMedia(textVal);
      }
    }, { signal });

    const initialVariantId = new URL(location.href).searchParams.get('variant');
    if (initialVariantId) {
      setTimeout(() => goToVariantMedia(initialVariantId), 50);
    }

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const index = Number(entry.target.getAttribute('data-slide-index') || 0);
              this.#setActiveThumb(index);
            }
          });
        },
        { root: this.#track, threshold: 0.5 }
      );

      this.#slides.forEach((slide) => this.#observer.observe(slide));
    }

    this.#track.addEventListener('scroll', () => {
      const slideWidth = this.#track.clientWidth;
      if (slideWidth > 0) {
        const activeIdx = Math.round(this.#track.scrollLeft / slideWidth);
        this.#setActiveThumb(activeIdx);
      }
    }, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goToSlide(index) {
    if (index < 0 || index >= this.#slides.length) return;
    const slide = this.#slides[index];
    if (slide && this.#track) {
      this.#track.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });
      this.#setActiveThumb(index);
    }
  }

  #step(direction) {
    if (!this.#track || this.#slides.length === 0) return;
    const currentScroll = this.#track.scrollLeft;
    const slideWidth = this.#track.clientWidth;
    const currentIndex = Math.round(currentScroll / (slideWidth || 1));
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = this.#slides.length - 1;
    if (nextIndex >= this.#slides.length) nextIndex = 0;
    this.goToSlide(nextIndex);
  }

  #setActiveThumb(index) {
    this.#thumbs.forEach((thumb, i) => {
      const isCurrent = i === index;
      thumb.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      if (isCurrent) {
        thumb.classList.add('border-accent', 'opacity-100', 'ring-1', 'ring-accent');
        thumb.classList.remove('border-transparent', 'opacity-60');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      } else {
        thumb.classList.remove('border-accent', 'opacity-100', 'ring-1', 'ring-accent');
        thumb.classList.add('border-transparent', 'opacity-60');
      }
    });
  }
}

if (!customElements.get('s-product-gallery-slider')) {
  customElements.define('s-product-gallery-slider', SProductGallerySlider);
}
